import { ApprovalRefundEnum } from 'api-hooks/common';
import {
  useGetRefund,
  useUpdateRefund,
  useUpdateRefundReleasePurchase,
  useUpdateRefundReleaseSale,
} from 'api-hooks/refund/refund';
import { Notification, KIND } from 'baseui/notification';
import { NavigationRoutes } from 'common/constants';
import LoadingComponent from 'components/common/loading';
import ErrorViewComponent from 'components/elements/error-view-component';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import useApprovalableEdit from 'hooks/use-approvalable-edit';
import useDismiss from 'hooks/use-dismiss';
import { DetailContainer, SeparatorVertical } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import * as React from 'react';
import { NavigationProps } from 'types';

import { SaleInvoiceRefundType } from './components/form';
import RefundAgentForm from './components/form/refund-agent-form';
import RefundCustomerForm from './components/form/refund-customer-form';
import RefundInformationForm from './components/form/refund-information-form';
import { Provider as RefundFormStateProvider } from '../hooks/use-refund-formstate';

//approvalRefundType: 'INFORMATION' | 'CUSTOMER' | 'AGENT'

export type RefundViewParams = {
  id: string;
  approvalRefundType?: ApprovalRefundEnum;
  invoice?: SaleInvoiceRefundType;
};

export default function RefundView(
  props: NavigationProps<NavigationRoutes.RefundView>,
) {
  const { query } = useRouter();
  const id = query?.id as string;
  const { data, error, isLoading, isFetching, refetch } = useGetRefund({
    id,
  });

  const approvalRefundType = props.navigation.params?.approvalRefundType;

  const { mutateAsync: updateRefund, error: refundError } = useUpdateRefund();
  const { mutateAsync: updateRefundPurchase, error: refundPurchaseError } =
    useUpdateRefundReleasePurchase();
  const { mutateAsync: updateRefundSale, error: refundSaleError } =
    useUpdateRefundReleaseSale();

  const onResolveRefund = useApprovalableEdit('refunds');
  const onResolveReleasePurchase = useApprovalableEdit('release-purchases');
  const onResolveReleaseSale = useApprovalableEdit('release-sales');
  const onDismiss = useDismiss('refunds');

  const refund = data?.data;

  const [editableInformation, setStateInformation] = React.useState(false);
  const [editableAgent, setStateAgent] = React.useState(false);
  const [editableCustomer, setStateCustomer] = React.useState(false);

  const setEditableForm = React.useCallback(
    (value: { information: boolean; customer: boolean; agent: boolean }) => {
      setStateInformation(value.information);
      setStateAgent(value.agent);
      setStateCustomer(value.customer);
    },
    [],
  );

  const value = React.useMemo(() => {
    return {
      refund,
      editableAgent,
      editableCustomer,
      editableInformation,
      setEditableForm,
    };
  }, [
    editableAgent,
    refund,
    editableCustomer,
    editableInformation,
    setEditableForm,
  ]);

  const errorMessage =
    refundError?.message ??
    refundSaleError?.message ??
    refundPurchaseError?.message;

  return (
    <RefundFormStateProvider value={value}>
      <FetchWrapperComponent
        onRetry={refetch}
        loadingComponent={<LoadingComponent />}
        error={error}
        errorComponent={
          <DetailContainer>
            <ErrorViewComponent refetch={refetch} />
          </DetailContainer>
        }
        isLoading={isLoading || isFetching}
        component={
          <>
            {!!errorMessage && (
              <>
                <Notification
                  overrides={{
                    Body: { style: { width: 'auto', wordBreak: 'break-all' } },
                  }}
                  kind={KIND.negative}
                  closeable
                >
                  {errorMessage}
                </Notification>
                <SeparatorVertical $margin={8} />
              </>
            )}
            <RefundInformationForm
              {...props}
              refund={refund}
              approvalRefundType={approvalRefundType}
              onSubmit={async (input, form) => {
                const result = await updateRefund({
                  id: refund!.id!,
                  body: input,
                });
                onResolveRefund(result);
                return result.data;
              }}
            />
            <RefundCustomerForm
              {...props}
              refund={refund}
              approvalRefundType={approvalRefundType}
              onSubmit={async (input, form) => {
                const result = await updateRefundSale({
                  id: refund!.releaseSale.id!,
                  body: input,
                });
                onDismiss(result.data.id);
                onResolveReleaseSale(result);
                return result.data;
              }}
            />
            <SeparatorVertical $margin={36} />
            {refund?.releasePurchase?.id && (
              <RefundAgentForm
                {...props}
                refund={refund}
                approvalRefundType={approvalRefundType}
                onSubmit={async (input, form) => {
                  const result = await updateRefundPurchase({
                    id: refund.releasePurchase?.id!,
                    body: input,
                  });
                  onDismiss(result.data.id);
                  onResolveReleasePurchase(result);
                  return result.data;
                }}
              />
            )}
          </>
        }
      />
    </RefundFormStateProvider>
  );
}
